import {
  Choice,
  FieldError,
  MoneyField,
  RATE_UNKNOWN,
  RateField,
  SelectField,
} from "@/components/fields";
import { useState } from "react";
import { money, rate } from "@/plan-logic/lib/format";
import {
  ACCOUNT_KIND_LABEL,
  type AccountKindKey,
  type DraftAccount,
  EVERYDAY_KINDS,
  INVESTMENT_KINDS,
  members,
  newAccount,
  type OnboardingDraft,
  personLabel,
} from "@/plan-logic/questionnaire/draft";
import { Screen, useOnbNav } from "../Screen";
import { removeAt, updateAt, useDraft } from "../useDraft";
import { ConnectPanel, FlinksPanel, WealthicaPanel } from "./Connect";

type Way = "connect" | "manual" | "none";

/**
 * Banking (v42–v47): connect through Flinks, or type it in instead. The two are mutually exclusive:
 * choosing one closes the other's panel. Connecting isn't built yet, so its panel shows the empty start
 * state with the button disabled, and Continue waits for "I'll type it in instead" (the default).
 */
export function BankingStep({ eyebrow }: { eyebrow: string }) {
  const nav = useOnbNav();
  const [way, setWay] = useState<Way>("manual");
  return (
    <Screen
      eyebrow={eyebrow}
      h1="onboarding.banking.h1"
      sub="onboarding.banking.sub"
      nextLabel={way === "manual" ? "Type them in" : "Continue"}
      nextDisabled={way === "connect"}
      onNext={() => nav.go("everyday-accounts")}
    >
      <div className="choices">
        <Choice
          selected={way === "connect"}
          onClick={() => setWay("connect")}
          icon="🏦"
          title="Connect my bank"
          tag="Coming soon"
          testId="connect-bank"
        >
          We can never move your money. The connection lets Abi compile your account statements, so
          your plan is built on what you actually earn and spend rather than an estimate.
        </Choice>
      </div>
      <FlinksPanel />
      <div className="choices" style={{ marginTop: 14 }}>
        <Choice
          selected={way === "manual"}
          onClick={() => setWay("manual")}
          icon="✎"
          title="I’ll type it in instead"
          testId="manual-bank"
        >
          If you would rather not connect, type in your accounts and what you spend in an average
          month. Your plan will be less precise, and you can connect through Flinks later.
        </Choice>
      </div>
      {way === "connect" && (
        <ConnectPanel
          via="Flinks"
          sub="One institution at a time. Connect one, then tell us whether there is another."
          connected={[]}
          testId="bank-list-card"
        />
      )}
    </Screen>
  );
}

/** Investments: connect through Wealthica, type them in, or none yet (mutually exclusive). */
export function InvestmentsStep({ eyebrow }: { eyebrow: string }) {
  const nav = useOnbNav();
  const { draft, patch } = useDraft();
  const [connect, setConnect] = useState(false);
  const way: Way = connect ? "connect" : draft.noInvestments ? "none" : "manual";
  return (
    <Screen
      eyebrow={eyebrow}
      h1="onboarding.investments.h1"
      sub="onboarding.investments.sub"
      nextDisabled={way === "connect"}
      onNext={() => nav.go(draft.noInvestments ? "spending" : "investment-accounts")}
    >
      <div className="choices">
        <Choice
          selected={way === "connect"}
          onClick={() => setConnect(true)}
          icon="📈"
          title="Connect my investments"
          tag="Coming soon"
          testId="connect-investments"
        >
          We can never move your money. Connecting lets Abi pull your account types, holdings and
          returns, so your dashboard runs on your real numbers. Covers 150+ Canadian banks and
          brokerages.
        </Choice>
      </div>
      <WealthicaPanel />
      <div className="choices" style={{ marginTop: 14 }}>
        <Choice
          selected={way === "manual"}
          onClick={() => {
            setConnect(false);
            patch("noInvestments", false);
          }}
          icon="✎"
          title="I’ll type it in instead"
          testId="manual-investments"
        >
          If you would rather not connect to Wealthica, enter your investment accounts. These
          figures will not update on their own, so keeping them current is on you, which can make
          your plan less accurate over time. You can always connect later.
        </Choice>
        <Choice
          selected={way === "none"}
          onClick={() => {
            setConnect(false);
            patch("noInvestments", true);
          }}
          icon="—"
          title="I don’t have any yet"
          testId="no-investments"
        >
          That&rsquo;s fine. Your plan will show you where to start.
        </Choice>
      </div>
      {way === "connect" && (
        <ConnectPanel
          via="Wealthica"
          sub="One institution at a time, the same as your everyday accounts."
          connected={[]}
          testId="inv-list-card"
        />
      )}
    </Screen>
  );
}

function AccountRow({
  a,
  i,
  kinds,
  draft,
  onChange,
  onRemove,
  err,
}: {
  a: DraftAccount;
  i: number;
  kinds: AccountKindKey[];
  draft: OnboardingDraft;
  onChange: (a: DraftAccount) => void;
  onRemove: () => void;
  err: (path: string) => string | undefined;
}) {
  const people = members(draft);
  const kids = draft.hasKids ? draft.kids : [];
  return (
    <div className="rowbox" data-testid={`account-${a.kind}`}>
      <div className="rowhead">
        <b>{a.name || ACCOUNT_KIND_LABEL[a.kind]}</b>
        <button type="button" className="rm" onClick={onRemove}>
          Remove
        </button>
      </div>
      <div className="two">
        <SelectField
          label="What kind"
          value={a.kind}
          options={kinds.map((k) => [k, ACCOUNT_KIND_LABEL[k]])}
          onChange={(kind) => onChange({ ...a, kind })}
          style={{ margin: 0 }}
        />
        <div className="field" style={{ margin: 0 }}>
          <label htmlFor={`acct-name-${a.uuid}`}>Name (optional)</label>
          <input
            id={`acct-name-${a.uuid}`}
            type="text"
            value={a.name}
            placeholder={`e.g. ${a.kind === "credit" ? "Visa" : "Simplii " + ACCOUNT_KIND_LABEL[a.kind]}`}
            onChange={(e) => onChange({ ...a, name: e.target.value })}
          />
        </div>
      </div>
      <div className="two" style={{ marginTop: 12 }}>
        {people.length > 1 ? (
          <SelectField
            label="Whose is it"
            value={a.ownerUuid}
            options={people.map((p, k) => [p.uuid, personLabel(p, k ? "Your partner" : "You")])}
            onChange={(ownerUuid) => onChange({ ...a, ownerUuid })}
            style={{ margin: 0 }}
          />
        ) : (
          <span />
        )}
        <MoneyField
          label={
            a.kind === "credit"
              ? "Balance owing today"
              : INVESTMENT_KINDS.includes(a.kind)
                ? "Roughly how much is in it"
                : "Balance today"
          }
          value={a.balance}
          onChange={(balance) => onChange({ ...a, balance })}
          required
          error={err(`accounts.${i}.balance`)}
          style={{ margin: 0 }}
          testId={`account-${i}-balance`}
        />
      </div>
      {a.kind === "credit" && (
        <div className="two" style={{ marginTop: 12 }}>
          <RateField
            label="Interest rate"
            value={a.rate}
            required={false}
            onChange={(r) => onChange({ ...a, rate: r })}
            placeholder={RATE_UNKNOWN}
            error={err(`accounts.${i}.rate`)}
            style={{ margin: 0 }}
            testId={`account-${i}-rate`}
          />
        </div>
      )}
      {a.kind === "grouprrsp" && (
        <p className="hint" style={{ margin: "8px 0 0" }}>
          If your employer matches what you put in, you&rsquo;ll add that with what you earn.
        </p>
      )}
      {(a.kind === "chequing" || a.kind === "savings") && (
        <div className="two" style={{ marginTop: 12 }}>
          <RateField
            label="Interest it pays (optional)"
            required={false}
            value={a.rate}
            onChange={(r) => onChange({ ...a, rate: r })}
            placeholder="e.g. 2.5%"
            style={{ margin: 0 }}
          />
        </div>
      )}
      {a.kind === "resp" && kids.length > 0 && (
        <div className="field" style={{ margin: "12px 0 0" }}>
          <label>Who is it for?</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {kids.map((k) => {
              const on = a.beneficiaryUuids.includes(k.uuid);
              return (
                <button
                  type="button"
                  key={k.uuid}
                  className={`chip${on ? " on" : ""}`}
                  aria-pressed={on}
                  onClick={() =>
                    onChange({
                      ...a,
                      beneficiaryUuids: on
                        ? a.beneficiaryUuids.filter((u) => u !== k.uuid)
                        : [...a.beneficiaryUuids, k.uuid],
                    })
                  }
                >
                  {k.givenName || "Your child"}
                </button>
              );
            })}
          </div>
          <FieldError error={err(`accounts.${i}.beneficiaryUuids`)} />
        </div>
      )}
    </div>
  );
}

function ManualAccounts({ kinds }: { kinds: AccountKindKey[] }) {
  const { draft, patch, err } = useDraft();
  const rows = draft.accounts.map((a, i) => ({ a, i })).filter(({ a }) => kinds.includes(a.kind));
  return (
    <div className="card pad">
      {!rows.length && <div className="empty">Nothing yet. Add your first account below.</div>}
      {rows.map(({ a, i }) => (
        <AccountRow
          key={a.uuid}
          a={a}
          i={i}
          kinds={kinds}
          draft={draft}
          onChange={(next) =>
            patch(
              "accounts",
              updateAt(draft.accounts, i, () => next),
            )
          }
          onRemove={() => patch("accounts", removeAt(draft.accounts, i))}
          err={err}
        />
      ))}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 9, marginTop: 6 }}>
        {kinds.map((k) => (
          <button
            type="button"
            key={k}
            className="mini"
            onClick={() => patch("accounts", [...draft.accounts, newAccount(k, draft.self.uuid)])}
            data-testid={`add-account-${k}`}
          >
            + {ACCOUNT_KIND_LABEL[k]}
          </button>
        ))}
      </div>
    </div>
  );
}

export function EverydayAccountsStep({ eyebrow }: { eyebrow: string }) {
  return (
    <Screen
      eyebrow={eyebrow}
      h1="onboarding.manualAccounts.h1"
      sub="onboarding.manualAccounts.sub"
      maxWidth={680}
    >
      <p className="eyebrow" style={{ margin: "0 0 10px" }}>
        Chequing, savings and credit cards
      </p>
      <ManualAccounts kinds={EVERYDAY_KINDS} />
    </Screen>
  );
}

export function InvestmentAccountsStep({ eyebrow }: { eyebrow: string }) {
  return (
    <Screen
      eyebrow={eyebrow}
      h1="onboarding.manualAccounts.h1"
      sub="onboarding.manualAccounts.sub"
      maxWidth={680}
    >
      <p className="eyebrow" style={{ margin: "0 0 10px" }}>
        RRSP, TFSA, FHSA, RESP and non-registered
      </p>
      <ManualAccounts kinds={INVESTMENT_KINDS} />
    </Screen>
  );
}

export function SpendingStep({ eyebrow }: { eyebrow: string }) {
  const { draft, patch, err } = useDraft();
  const s = draft.spending;
  const total = (s.essential ?? 0) + (s.lifestyle ?? 0) + (s.rent ?? 0);
  return (
    <Screen eyebrow={eyebrow} h1="onboarding.spending.h1" sub="onboarding.spending.sub">
      <div className="card pad">
        <MoneyField
          label="Essentials, a month"
          value={s.essential}
          onChange={(v) => patch("spending", { ...s, essential: v })}
          required
          error={err("spending.essential")}
          hint="Groceries, utilities, insurance, getting around, childcare, health: the bills that never stop. Leave out your mortgage and debt payments; we ask about those separately."
          testId="spend-essential"
        />
        <MoneyField
          label="Lifestyle, a month"
          value={s.lifestyle}
          onChange={(v) => patch("spending", { ...s, lifestyle: v })}
          required
          error={err("spending.lifestyle")}
          hint="Eating out, shopping, subscriptions, travel and everything else you could cut if you had to."
          testId="spend-lifestyle"
        />
        <MoneyField
          label="Rent, a month (if you rent)"
          value={s.rent}
          onChange={(v) => patch("spending", { ...s, rent: v })}
          error={err("spending.rent")}
          placeholder="0"
          testId="spend-rent"
        />
        <div className="spendtot">
          <span>Every month</span>
          <span className="amt">{money(total)}</span>
        </div>
      </div>
    </Screen>
  );
}

const BADGE: Record<AccountKindKey, string> = {
  chequing: "#1B4D3E",
  savings: "#1B4D3E",
  credit: "#B03A6E",
  rrsp: "#0B2A5B",
  grouprrsp: "#0B2A5B",
  tfsa: "#0B2A5B",
  fhsa: "#0B2A5B",
  resp: "#0B2A5B",
  nonreg: "#0B2A5B",
};

/** "What we found" becomes a review of what the user typed in. */
export function ReviewStep({ eyebrow }: { eyebrow: string }) {
  const { draft } = useDraft();
  const nav = useOnbNav();
  const people = members(draft);
  const owner = (uuid: string) => {
    const i = people.findIndex((p) => p.uuid === uuid);
    return i < 0 ? "" : personLabel(people[i], i ? "Your partner" : "You");
  };
  const row = (a: DraftAccount) => (
    <div className="acct" key={a.uuid}>
      <span className="bank" style={{ background: BADGE[a.kind] }}>
        {ACCOUNT_KIND_LABEL[a.kind].slice(0, 2).toUpperCase()}
      </span>
      <span>
        <span className="nm">{a.name || ACCOUNT_KIND_LABEL[a.kind]}</span>
        <span className="ty">
          {owner(a.ownerUuid)}
          {a.kind === "credit" && a.rate !== undefined ? ` · ${rate(a.rate)} interest` : ""}
          {a.kind !== "credit" && a.name ? ` · ${ACCOUNT_KIND_LABEL[a.kind]}` : ""}
        </span>
      </span>
      <span className="amt">
        {a.kind === "credit" ? money(-(a.balance ?? 0)) : money(a.balance)}
      </span>
    </div>
  );
  const everyday = draft.accounts.filter((a) => EVERYDAY_KINDS.includes(a.kind));
  const invest = draft.noInvestments
    ? []
    : draft.accounts.filter((a) => INVESTMENT_KINDS.includes(a.kind));
  const s = draft.spending;
  const edit = (step: string) => (
    <button type="button" className="linkbtn" onClick={() => nav.go(step)}>
      Change
    </button>
  );
  const n = everyday.length + invest.length;
  return (
    <Screen eyebrow={eyebrow} h1="onboarding.found.h1" sub="onboarding.found.sub">
      <div className="note-soft">
        {n} account{n === 1 ? "" : "s"} and a monthly spending estimate. If something looks off or
        an account is missing, change it now or straight after we build your plan.
      </div>
      <div className="card pad">
        <div className="chead">
          <h2>Everyday money</h2>
          {edit("everyday-accounts")}
        </div>
        <div style={{ marginTop: 10 }}>
          {everyday.length ? everyday.map(row) : <p className="mut">No accounts added.</p>}
        </div>
      </div>
      <div className="card pad mt">
        <div className="chead">
          <h2>Investments</h2>
          {edit("investments")}
        </div>
        <div style={{ marginTop: 10 }}>
          {invest.length ? invest.map(row) : <p className="mut">None yet.</p>}
        </div>
      </div>
      <div className="card pad mt">
        <div className="chead">
          <h2>What you spend in a month</h2>
          {edit("spending")}
        </div>
        <div className="spendrow">
          <span className="sw" style={{ background: "var(--c-ess)" }} />
          <span>Essentials</span>
          <span className="amt">{money(s.essential ?? 0)}</span>
        </div>
        <div className="spendrow">
          <span className="sw" style={{ background: "var(--c-life)" }} />
          <span>Lifestyle</span>
          <span className="amt">{money(s.lifestyle ?? 0)}</span>
        </div>
        {!!s.rent && (
          <div className="spendrow">
            <span className="sw" style={{ background: "var(--c-ess)" }} />
            <span>Rent</span>
            <span className="amt">{money(s.rent)}</span>
          </div>
        )}
        <div className="spendtot">
          <span>Every month</span>
          <span className="amt">
            {money((s.essential ?? 0) + (s.lifestyle ?? 0) + (s.rent ?? 0))}
          </span>
        </div>
      </div>
    </Screen>
  );
}
